/** The order record editor's binding of the view-agnostic record core
 *  (`features/records/editor`) — everything FieldSlot and the Ledger need to know about
 *  an order that the catalog alone does not say (plan §2.4, D10).
 *
 *  - Verdicts: edit_context `restech.verdicts`, the server's, as they stand (an order
 *    Ventrata created has its order webhook's columns rewritten on every update; booking
 *    imports fill a column only while it is empty). The ledger banner adds what the feed
 *    does to the order as a whole — "Ventrata overwrites 20 order fields on every update
 *    of the order" — from `restech.rewritten_fields`.
 *  - Editability: the ConsoleOrderEditEnabled kill switch and console_orders_edit.
 *  - D10: an order-ref change raises the required "Linked bookings" question (no
 *    default). Both answers' consequences stay visible under the question; "update"
 *    writes the linked bookings, so without console_bookings_edit it is shown disabled
 *    with the reason (edit_context does not carry that permission — the operator's own
 *    permission list does). A changed field with a catalog warning carries it under the
 *    field and on its ledger row: the machine ID's "it is Ventrata's order key", the
 *    account's "changes who order events address", the state's "no cascade" (the order
 *    ref has none: its amber undo marker and the question say it all).
 *  - Pickers: the account among the users of the order's tenant family (a licensee's
 *    guests live with its franchise parent), the order tags among the tenant's tags —
 *    each option with its tenant + pk.
 *  - Preview and apply: `useOrderChangesetPreview` / `useApplyOrderChangeset`.
 */
import { useMemo } from 'react';

import { EVENT_STATUS_LABELS } from '@/api/bookingEdit';
import { ownerOptions, tagOptions } from '@/api/bookings';
import {
  useApplyOrderChangeset,
  useOrderChangesetPreview,
  type OrderRecordCatalog,
  type OrderRecordCatalogField,
  type OrderRecordCatalogOption,
  type OrderRecordEditContext,
} from '@/api/orderRecord';
import type { OrderDetail } from '@/api/orders';
import { ownerRef } from '@/features/bookings/record/ChangeOwnerWidget';
import { recordKey, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';
import type {
  RecordEditorConfig,
  RecordField,
  RecordOption,
  RecordOptionRef,
  RecordVerdict,
  RecordWidget,
} from '@/features/records/editor/types';
import { createRecordPicker, createTagsWidget } from '@/features/records/fields/widgets/pickers';
import { EDITING_OFF } from '@/features/records/header/editingOff';

import { orderManagedBy } from './views/spineLayout';
import { ORDER_TIMEZONE } from './views/spineModel';

/** The D10 question an order-ref change raises. */
export const LINKED_BOOKINGS_OPTION = 'linked_bookings_ref';
/** Its answer that writes the linked bookings (needs console_bookings_edit). */
export const LINKED_UPDATE = 'update';
export const BOOKINGS_EDIT_PERM = 'console_bookings_edit';
/** Below this many characters the account search does not run (the users table is large). */
const MIN_ACCOUNT_QUERY = 2;

function personName(u: { first_name?: string | null; last_name?: string | null } | null | undefined): string {
  return [u?.first_name, u?.last_name].filter(Boolean).join(' ');
}

/** A catalog field in the records core's vocabulary: an order field has no guards, no
 *  permissions beyond console_orders_edit and no lead; its static rule is the writer
 *  that feeds this order (Ventrata's webhook, else booking imports). */
function recordField(f: OrderRecordCatalogField, ventrata: boolean): RecordField {
  return {
    ...f,
    guards: [],
    permissions: [],
    follows_lead: false,
    restech_rule: ventrata ? f.ventrata_rule : f.import_rule,
  };
}

function linkedLine(ctx: OrderRecordEditContext): string {
  const { count, ids } = ctx.linked_bookings;
  if (count === 0) return 'No bookings are linked to this order.';
  const shown = ids.slice(0, 6).map((id) => `#${id}`).join(', ');
  const more = count > 6 ? ` and ${count - 6} more` : '';
  return `${count} ${count === 1 ? 'booking is' : 'bookings are'} linked to this order: ${shown}${more}.`;
}

export interface OrderEditorConfigInput {
  catalog: OrderRecordCatalog;
  editContext: OrderRecordEditContext;
  /** The order detail — names of its account and tags. */
  detail?: Pick<OrderDetail, 'tags' | 'user'> | null;
  /** The operator may edit bookings (console_bookings_edit) — what "update the linked
   *  bookings" needs. edit_context's `permissions` wins when it states it. */
  canEditBookings: boolean;
  /** A ledger row for a field not on screen (another page tab). */
  onFocusMissing?: (rec: RecordKey, path: string) => void;
}

/** The pure half of the config (everything but the hooks and pickers), for tests. */
export function orderEditorConfigParts({
  catalog,
  editContext: ctx,
  detail,
  canEditBookings,
  onFocusMissing,
}: OrderEditorConfigInput): Omit<RecordEditorConfig, 'usePreview' | 'useApply' | 'widgets'> {
  const orderRec = recordKey('order', ctx.order_id);
  const restech = ctx.restech;
  const managedBy = orderManagedBy(ctx);
  const ventrata = restech.managed_by === 'ventrata';
  const fields = catalog.fields.map((f) => recordField(f, ventrata));
  const fieldByKey = new Map(fields.map((f) => [f.key, f]));

  const mayUpdateBookings = ctx.permissions[BOOKINGS_EDIT_PERM] ?? canEditBookings;
  const options: RecordOption[] = catalog.options.map((o: OrderRecordCatalogOption) =>
    o.key === LINKED_BOOKINGS_OPTION
      ? {
          ...o,
          help: [linkedLine(ctx), o.help].filter(Boolean).join(' '),
          values: o.values.map((v) =>
            v.value === LINKED_UPDATE && !mayUpdateBookings
              ? {
                  ...v,
                  disabled: true,
                  disabledReason: `Needs the ${BOOKINGS_EDIT_PERM} permission — it writes the linked bookings`,
                }
              : v,
          ),
        }
      : o,
  );

  const verdictOf = (rec: RecordKey, field: RecordField): RecordVerdict | null =>
    rec === orderRec ? (restech.verdicts[field.key] ?? null) : null;

  const editabilityOf = () => {
    if (!catalog.enabled || !ctx.flags.order_edit_enabled) {
      return { editable: false, reason: EDITING_OFF };
    }
    if (!ctx.permissions.console_orders_edit) {
      return { editable: false, reason: 'Needs the console_orders_edit permission' };
    }
    return { editable: true };
  };

  const changeWarnings = (rec: RecordKey, path: string): string[] => {
    if (rec !== orderRec) return [];
    const warning = fieldByKey.get(path)?.warning;
    return warning ? [warning] : [];
  };

  const ref = ctx.baselines[orderRec]?.order_ref;
  const groupOf = () => ({
    id: 'order',
    title: `Order ${typeof ref === 'string' && ref ? ref : `#${ctx.order_id}`}`,
  });

  const formatValue = (field: RecordField, _rec: RecordKey, value: unknown): string | undefined => {
    if (field.key === 'user' && (value === null || value === undefined)) return 'No account';
    return undefined;
  };

  const account = detail?.user ?? null;
  const optionRefs: Record<string, RecordOptionRef[]> = {
    tags: (detail?.tags ?? []).map((t) => ({
      id: t.id,
      label: t.name,
      group: t.category?.name ?? null,
      tenant: ctx.tenant.code,
    })),
    ...(account
      ? {
          user: [
            {
              id: account.id,
              label: personName(account) || account.email || `#${account.id}`,
              extra: [personName(account) ? account.email : null],
            },
          ],
        }
      : {}),
  };

  // An account-only change has no diff for order.updated (its context leaves the account
  // out), so it emits nothing; the linked bookings' events come with the preview.
  const estimateEvents = (state: DraftState) => {
    const paths = Object.keys(state.entries[orderRec] ?? {});
    return paths.some((p) => p !== 'user') ? [{ code: 'order.updated', guest_visible: false }] : [];
  };

  const rewritten = restech.rewritten_fields.length;
  return {
    fields,
    options,
    actions: [],
    effects: catalog.effects,
    timezone: ORDER_TIMEZONE,
    recordNoun: 'order',
    managedBy,
    ...(managedBy && rewritten > 0
      ? { managedNote: `${managedBy} overwrites ${rewritten} order ${rewritten === 1 ? 'field' : 'fields'} on every update of the order` }
      : {}),
    verdictOf,
    editabilityOf,
    changeWarnings,
    groupOf,
    recordOrder: [orderRec],
    formatValue,
    provenanceOf: (field) =>
      field.kind === 'vendor'
        ? `Set by ${managedBy ?? 'the booking system'}`
        : [field.restech_note, field.help].filter(Boolean).join(' ') || undefined,
    optionRefs,
    estimateEvents,
    confirmFollowUps: [],
    eventStatusLabel: (s) => EVENT_STATUS_LABELS[s] ?? s.replace(/_/g, ' '),
    maxChanges: catalog.max_changes,
    descriptionMaxLength: catalog.description_max_length,
    restechHelpStory: 'order-detail',
    ...(onFocusMissing ? { onFocusMissing } : {}),
  };
}

/** The order's remote pickers: the account among its tenant family's users, the tags
 *  among its tenant's — with tenant + pk on every option. */
export function orderWidgets(ctx: Pick<OrderRecordEditContext, 'tenant'>): Record<string, RecordWidget> {
  const tenantId = ctx.tenant.id;
  // A franchise licensee's guests belong to its parent operator.
  const accountTenantId = ctx.tenant.parent_id ?? tenantId;
  return {
    user: createRecordPicker({
      load: async (_f, _r, q, signal) =>
        q.trim().length < MIN_ACCOUNT_QUERY ? [] : (await ownerOptions(accountTenantId, q, signal)).map(ownerRef),
      placeholder: 'No account — type a name or email',
    }),
    tags: createTagsWidget({
      load: async (_f, _r, q, signal) =>
        (await tagOptions(tenantId, q, undefined, signal)).map((t) => ({
          id: t.id,
          label: t.name,
          group: t.category.name,
          tenant: t.tenant?.code ?? null,
        })),
    }),
  };
}

/** The order editor's `RecordEditorConfig` — stable while its inputs are. */
export function useOrderRecordEditorConfig(input: OrderEditorConfigInput): RecordEditorConfig {
  const { catalog, editContext, detail, canEditBookings, onFocusMissing } = input;
  const id = editContext.order_id;
  const tenantId = editContext.tenant.id;
  const parentId = editContext.tenant.parent_id;
  const widgets = useMemo(
    () => orderWidgets({ tenant: editContext.tenant }),
    // The pickers depend on the tenant (and its parent) only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId, parentId],
  );
  return useMemo<RecordEditorConfig>(
    () => ({
      ...orderEditorConfigParts({ catalog, editContext, detail, canEditBookings, onFocusMissing }),
      widgets,
      usePreview: (body) => useOrderChangesetPreview(id, body),
      useApply: (opts) => useApplyOrderChangeset(id, opts),
    }),
    [catalog, editContext, detail, canEditBookings, onFocusMissing, widgets, id],
  );
}
